// eventConflicts — the three display-only flags on an event (Stage 6).
//
//   bookings  passengers are booked on a cruise and deck this event covers,
//             on a day it covers. Counted the way Home counts capacity:
//             every booking that is not Cancelled or FOC.
//   online    a day this event covers is still open to online booking. Only
//             asked for days inside the public window (today + 7): beyond it
//             nobody can book anyway. The answer comes from the public site's
//             availability edge function, the same call the Home strip makes;
//             nothing about availability is computed here. A covered slot
//             that is not "closed" counts as open (sold out is not closed).
//   sailing   the event's own hours overlap a scheduled sailing that day, read
//             from cruise_details_on() so a scheduled time change is honoured.
//             The cruises the event sits on are expected to overlap and are
//             left out; if the RPC fails there is no flag rather than a guess
//             from the standing table.
//
// Nothing here blocks, closes or writes. Every check is bounded by the dates
// of the events passed in, fails quietly on its own, and is meant to run
// after the screen has painted.

import { supabase } from '../lib/supabase';
import { CUSTOMER_WINDOW_DAYS, addDaysIso } from './bookingWindow';

const MAX_DAYS_PER_EVENT = 62;
const DATE_CHUNK = 100;
const CACHE_TTL_MS = 60 * 1000;

const isoToDmy = (iso) => { const [y, m, d] = iso.split('-'); return `${d}-${m}-${y}`; };
const hhmm = (t) => (t ? String(t).slice(0, 5) : '');
const fmt12 = (t) => {
  const [h, m] = hhmm(t).split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return '';
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export const eventDays = (ev) => {
  const days = [];
  const last = ev.end_date || ev.event_date;
  for (let d = ev.event_date, i = 0; d <= last && i < MAX_DAYS_PER_EVENT; d = addDaysIso(d, 1), i++) days.push(d);
  return days;
};
const coversDeck = (ev, deck) => {
  const decks = ev.decks || [];
  return decks.length === 0 || decks.includes(deck) || decks.includes('Both');
};
const coversSlot = (ev, cruise, deck) => (ev.cruise_types || []).includes(cruise) && coversDeck(ev, deck);

// ── 1. bookings on what the event covers ─────────────────────────────────────
async function bookingsFlags(events) {
  const withCruises = events.filter(ev => (ev.cruise_types || []).length);
  if (!withCruises.length) return new Map();
  const dates = [...new Set(withCruises.flatMap(ev => eventDays(ev).map(isoToDmy)))];
  const rows = [];
  for (let i = 0; i < dates.length; i += DATE_CHUNK) {
    const { data, error } = await supabase
      .from('bookings')
      .select('date, cruise_type, deck, adults, children, payment_status')
      .in('date', dates.slice(i, i + DATE_CHUNK))
      .limit(5000);
    if (error) throw error;
    rows.push(...(data || []));
  }
  const boarding = rows.filter(b => b.payment_status !== 'Cancelled' && b.payment_status !== 'FOC');
  const out = new Map();
  withCruises.forEach(ev => {
    const days = new Set(eventDays(ev).map(isoToDmy));
    const bySlot = new Map();
    let total = 0;
    boarding.forEach(b => {
      if (!days.has(b.date) || !coversSlot(ev, b.cruise_type, b.deck)) return;
      const pax = (b.adults || 0) + (b.children || 0);
      const key = `${b.cruise_type} ${b.deck}`;
      bySlot.set(key, (bySlot.get(key) || 0) + pax);
      total += pax;
    });
    if (!total) return;
    const slots = [...bySlot.entries()].map(([k, n]) => `${n} on ${k}`).join(', ');
    out.set(ev.id, { kind: 'bookings', text: `${plural(total, 'passenger', 'passengers')} booked on what this event covers (${slots}).` });
  });
  return out;
}

// ── 2. still open to online booking ──────────────────────────────────────────
const availabilityCache = new Map(); // iso → { fetchedAt, cruises }
async function availabilityOn(iso) {
  const hit = availabilityCache.get(iso);
  if (hit && Date.now() - hit.fetchedAt < CACHE_TTL_MS) return hit.cruises;
  const { data, error } = await supabase.functions.invoke('availability', { body: { date: iso, adults: 1, children: 0 } });
  if (error || !data || data.error) throw new Error(data?.error || error?.message || 'availability failed');
  const cruises = data.cruises || [];
  availabilityCache.set(iso, { fetchedAt: Date.now(), cruises });
  return cruises;
}
async function onlineFlags(events, today) {
  const windowEnd = addDaysIso(today, CUSTOMER_WINDOW_DAYS);
  const inWindow = (iso) => iso >= today && iso <= windowEnd;
  const candidates = events.filter(ev => (ev.cruise_types || []).length && eventDays(ev).some(inWindow));
  if (!candidates.length) return new Map();
  const dates = [...new Set(candidates.flatMap(ev => eventDays(ev).filter(inWindow)))];
  const byDate = new Map();
  await Promise.all(dates.map(async (iso) => { try { byDate.set(iso, await availabilityOn(iso)); } catch (e) { /* no answer, no flag */ } }));
  const out = new Map();
  candidates.forEach(ev => {
    const open = new Set();
    eventDays(ev).filter(inWindow).forEach(iso => {
      (byDate.get(iso) || []).forEach(c => {
        if (c.status !== 'closed' && coversSlot(ev, c.cruiseName, c.deck)) open.add(`${c.cruiseName} ${c.deck}`);
      });
    });
    if (!open.size) return;
    out.set(ev.id, { kind: 'online', text: `Still open to online booking (${[...open].join(', ')}).` });
  });
  return out;
}

// ── 3. hours overlap a scheduled sailing ─────────────────────────────────────
const timesCache = new Map(); // iso → { fetchedAt, rows }
async function sailingsOn(iso) {
  const hit = timesCache.get(iso);
  if (hit && Date.now() - hit.fetchedAt < CACHE_TTL_MS) return hit.rows;
  const { data, error } = await supabase.rpc('cruise_details_on', { p_date: iso });
  if (error || !Array.isArray(data)) throw new Error(error?.message || 'cruise_details_on failed');
  timesCache.set(iso, { fetchedAt: Date.now(), rows: data });
  return data;
}
async function sailingFlags(events) {
  const timed = events.filter(ev => ev.start_time);
  if (!timed.length) return new Map();
  const dates = [...new Set(timed.flatMap(eventDays))];
  const byDate = new Map();
  await Promise.all(dates.map(async (iso) => { try { byDate.set(iso, await sailingsOn(iso)); } catch (e) { /* no answer, no flag */ } }));
  const out = new Map();
  timed.forEach(ev => {
    const from = hhmm(ev.start_time);
    const to = hhmm(ev.end_time) || from;
    const hits = new Map();
    eventDays(ev).forEach(iso => {
      (byDate.get(iso) || []).forEach(c => {
        if ((ev.cruise_types || []).includes(c.cruise_name)) return;        // sits on it: expected
        const dep = hhmm(c.departure_time), end = hhmm(c.end_time);
        if (!dep || !end) return;
        if (from <= end && to >= dep) hits.set(c.cruise_name, `${c.cruise_name} ${fmt12(dep)} to ${fmt12(end)}`);
      });
    });
    if (!hits.size) return;
    out.set(ev.id, { kind: 'sailing', text: `Hours overlap a scheduled sailing (${[...hits.values()].join('; ')}).` });
  });
  return out;
}

/**
 * Flags for the events given. Returns Map<event id, Array<{ kind, text }>>;
 * events with nothing to say are absent. Each of the three checks fails on
 * its own and quietly.
 */
export async function checkEventConflicts(events, { today }) {
  const live = events.filter(ev => ev.status !== 'cancelled' && ev.status !== 'completed' && (ev.end_date || ev.event_date) >= today);
  if (!live.length) return new Map();
  const [b, o, s] = await Promise.all([
    bookingsFlags(live).catch(() => new Map()),
    onlineFlags(live, today).catch(() => new Map()),
    sailingFlags(live).catch(() => new Map()),
  ]);
  const out = new Map();
  live.forEach(ev => {
    const lines = [b.get(ev.id), o.get(ev.id), s.get(ev.id)].filter(Boolean);
    if (lines.length) out.set(ev.id, lines);
  });
  return out;
}
